import Phaser from 'phaser';
import type { Chart, ChartNote, JudgmentGrade, Lane } from '@/types';
import { FoodNote } from '@/game/objects/FoodNote';
import type { Cat } from '@/game/objects/Cat';
import type { RhythmEngine } from './RhythmEngine';
import { resolveFoodForNote } from '@/data/foods';
import {
  CATCH_LINE_Y,
  FOOD_HITBOX_HALF_WIDTH_PX,
  LANE_WIDTH,
  PERFECT_POSITION_FRACTION,
  SPAWN_Y,
  TIMING_WINDOWS,
} from '@/game/config/GameConfig';
import { computeJudgmentForPosition } from '@/utils/rhythmMath';

function laneOffsetPx(lane: Lane): number {
  return lane === 'left' ? 0 : LANE_WIDTH;
}

export type JudgmentCallback = (note: ChartNote, grade: JudgmentGrade, diffMs: number) => void;

/**
 * Owns the falling-food lifecycle: spawning notes ahead of their target time (derived
 * from RhythmEngine, never from a local timer), driving their fall position every
 * frame, and resolving each one into a hit or miss judgment based on song-time
 * proximity plus horizontal overlap with the relevant cat's catch zone. Objects are
 * pooled so a full song never allocates more FoodNote instances than are ever
 * simultaneously on screen.
 */
export class NoteManager {
  private notes: ChartNote[];
  private nextSpawnIndex = 0;
  private pool: FoodNote[] = [];
  private active: FoodNote[] = [];

  constructor(
    private scene: Phaser.Scene,
    private rhythmEngine: RhythmEngine,
    chart: Chart,
    private cats: Record<Lane, Cat>,
    private onJudgment: JudgmentCallback,
    private enabledFoodIds: string[]
  ) {
    this.notes = [...chart.notes].sort((a, b) => a.time - b.time);
  }

  private acquireFoodNote(): FoodNote {
    const reusable = this.pool.find((f) => !f.visible);
    if (reusable) return reusable;
    const created = new FoodNote(this.scene);
    this.pool.push(created);
    return created;
  }

  update(): void {
    const currentTime = this.rhythmEngine.currentSongTime;
    const travelTime = this.rhythmEngine.noteTravelTime;

    while (this.nextSpawnIndex < this.notes.length && this.notes[this.nextSpawnIndex].time - travelTime <= currentTime) {
      const chartNote = this.notes[this.nextSpawnIndex];
      const food = this.acquireFoodNote();
      const xPx = laneOffsetPx(chartNote.lane) + chartNote.x * LANE_WIDTH;
      food.spawn(chartNote, resolveFoodForNote(chartNote.type, this.enabledFoodIds), xPx);
      this.active.push(food);
      this.nextSpawnIndex++;
    }

    const nowMs = this.scene.time.now;
    for (let i = this.active.length - 1; i >= 0; i--) {
      const food = this.active[i];
      const note = food.chartNote;
      if (!note) {
        this.active.splice(i, 1);
        continue;
      }

      if (food.resolved) {
        if (!food.visible) this.active.splice(i, 1);
        continue;
      }

      const y = this.rhythmEngine.getNoteY(note.time, SPAWN_Y, CATCH_LINE_Y);
      food.updateFall(y, nowMs);

      const diffMs = (currentTime - note.time) * 1000;
      const zone = this.cats[note.lane].getCatchZone();
      const catchHalfWidthPx = zone.halfWidth + FOOD_HITBOX_HALF_WIDTH_PX;

      // Track the best-centered moment the cat has overlapped this note (not the
      // first legal moment) -- timing can't distinguish skill here, since a cat
      // parked in place before the window opens will always pass through the exact
      // target instant regardless of arrival time. Positional precision is graded
      // instead (see computeJudgmentForPosition); timing only gates hit vs miss.
      if (diffMs >= -TIMING_WINDOWS.goodMs && diffMs <= TIMING_WINDOWS.missGraceMs) {
        if (!food.anticipationTriggered) {
          food.anticipationTriggered = true;
          this.cats[note.lane].playAnticipate();
        }

        const positionError = Math.abs(food.getWorldX() - zone.x);
        const overlap = positionError <= catchHalfWidthPx;

        if (overlap) {
          if (food.bestPositionError === null || positionError < food.bestPositionError) {
            food.bestPositionError = positionError;
            food.bestPositionDiffMs = diffMs;
          }
          // Once the note's target time has arrived and the cat is still in contact,
          // resolve now for instant feedback instead of waiting out the full window.
          if (diffMs >= 0) {
            this.resolveHit(food, note, catchHalfWidthPx);
            continue;
          }
        }
      }

      if (diffMs > TIMING_WINDOWS.missGraceMs) {
        if (food.bestPositionError !== null) {
          this.resolveHit(food, note, catchHalfWidthPx);
        } else {
          this.onJudgment(note, 'miss', diffMs);
          food.playMissed(() => food.reset());
        }
      }
    }
  }

  private resolveHit(food: FoodNote, note: ChartNote, catchHalfWidthPx: number): void {
    if (food.bestPositionError === null) return;
    const grade = computeJudgmentForPosition(food.bestPositionError, catchHalfWidthPx, PERFECT_POSITION_FRACTION);
    this.onJudgment(note, grade, food.bestPositionDiffMs);
    food.playCaught(() => food.reset());
  }

  isComplete(): boolean {
    return this.nextSpawnIndex >= this.notes.length && this.active.every((f) => f.resolved && !f.visible);
  }

  totalNotes(): number {
    return this.notes.length;
  }

  activeCount(): number {
    return this.active.length;
  }

  destroy(): void {
    this.pool.forEach((f) => f.destroy());
    this.pool = [];
    this.active = [];
  }
}
